import { buildMediaPreview } from "@coxauto-ui/cxm/communications/util";
import { MutableRefObject } from "react";

export interface MediaWidgetNodeViewOptions {
  onMediaTitleChangeRef: MutableRefObject<
    | ((endlinkUrl: string, mediaId: string, newTitle: string) => void)
    | undefined
  >;
}

/**
 * ProseMirror NodeView factory for `mediaWidget` nodes.
 *
 * Renders one `buildMediaPreview` card per `<img>` in the stored `html` attr.
 * Each card's title bar supports double-click inline editing:
 *  - Enter or blur → commit new title
 *  - Escape          → revert, no dispatch
 *
 * On commit the factory:
 *  1. Updates `data-media-title` + `alt` on the target `<img>` in the stored HTML
 *  2. Dispatches a `setNodeMarkup` transaction so ProseMirror (and Kendo's
 *     `onChange`) see the updated node
 *  3. Calls `onMediaTitleChangeRef.current` so the caller can sync
 *     `EndlinkMediaMap.titles` for the send-time metadata payload
 */
export function createMediaWidgetNodeView(options: MediaWidgetNodeViewOptions) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (node: any, editorView: any, getPos: any) => {
    const inertDoc = new DOMParser().parseFromString(
      node.attrs.html || "<span></span>",
      "text/html"
    );
    const sourceEl = (inertDoc.body.firstElementChild ??
      inertDoc.body) as HTMLElement;
    const mediaImages = Array.from(
      sourceEl.querySelectorAll<HTMLImageElement>("img")
    );

    // Constrained wrapper so the "×" delete button sits flush with the card's
    // top-right corner rather than the editor's right edge.
    const wrapper = document.createElement("span");
    wrapper.style.cssText =
      "position:relative;display:block;max-width:320px;margin:0 auto;";

    const previewCards: HTMLElement[] = [];

    for (let imgIdx = 0; imgIdx < mediaImages.length; imgIdx++) {
      const img = mediaImages[imgIdx];
      const preview = buildMediaPreview(document, img);

      attachTitleEditor(preview, imgIdx, node, editorView, getPos, options);

      previewCards.push(preview);
      wrapper.appendChild(preview);
    }

    // Always-visible "×" delete button (editor-only, not in serialized HTML).
    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.textContent = "×";
    deleteBtn.style.cssText =
      "position:absolute;top:-8px;right:-8px;width:20px;height:20px;" +
      "border-radius:50%;border:1px solid #ccc;background:#fff;color:#666;" +
      "font-size:14px;line-height:18px;text-align:center;cursor:pointer;" +
      "padding:0;z-index:1;box-shadow:0 1px 3px rgba(0,0,0,0.15);";
    deleteBtn.addEventListener("mouseenter", () => {
      deleteBtn.style.background = "#f44336";
      deleteBtn.style.color = "#fff";
      deleteBtn.style.borderColor = "#f44336";
    });
    deleteBtn.addEventListener("mouseleave", () => {
      deleteBtn.style.background = "#fff";
      deleteBtn.style.color = "#666";
      deleteBtn.style.borderColor = "#ccc";
    });
    deleteBtn.addEventListener("mousedown", e => {
      e.preventDefault();
      e.stopPropagation();
      const pos = typeof getPos === "function" ? getPos() : null;
      if (pos != null && editorView) {
        const tr = editorView.state.tr.delete(pos, pos + node.nodeSize);
        editorView.dispatch(tr);
      }
    });
    wrapper.appendChild(deleteBtn);

    return {
      dom: wrapper,
      // Allow mouse events so ProseMirror can create a NodeSelection.
      // Block keyboard/input events to prevent editing inside the widget.
      stopEvent: (event: Event) => !(event instanceof MouseEvent),
      ignoreMutation: () => true,
      selectNode() {
        for (const card of previewCards) {
          card.style.outline = "3px solid #0066cc";
          card.style.boxShadow = "0 0 0 4px rgba(0, 102, 204, 0.25)";
        }
      },
      deselectNode() {
        for (const card of previewCards) {
          card.style.outline = "";
          card.style.boxShadow = "";
        }
      }
    };
  };
}

/**
 * Wires up double-click inline title editing on a single preview card.
 *
 * `buildMediaPreview` appends the title bar as the last child of the card,
 * so `preview.lastElementChild` is always the title bar div.
 */
function attachTitleEditor(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  preview: HTMLElement,
  imgIdx: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorView: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPos: any,
  options: MediaWidgetNodeViewOptions
) {
  const titleBar = preview.lastElementChild as HTMLElement | null;
  if (!titleBar) return;

  titleBar.style.cursor = "text";
  titleBar.title = "Double-click to edit title";

  titleBar.addEventListener("dblclick", e => {
    e.stopPropagation();

    const currentTitle = titleBar.textContent ?? "";

    const input = document.createElement("input");
    input.type = "text";
    input.value = currentTitle;
    input.style.cssText =
      "width:100%;font-family:Arial,sans-serif;font-size:18px;font-weight:bold;" +
      "color:#10233b;background:transparent;border:none;" +
      "border-bottom:2px solid #0066cc;outline:none;" +
      "text-align:center;padding:0;box-sizing:border-box;";

    titleBar.textContent = "";
    titleBar.appendChild(input);
    input.focus();
    input.select();

    let committed = false;

    const commit = () => {
      if (committed) return;
      committed = true;

      const newTitle = input.value.trim() || currentTitle;
      titleBar.textContent = newTitle;

      if (newTitle === currentTitle) return;

      const pos = typeof getPos === "function" ? getPos() : null;
      if (pos == null || !editorView) return;

      const currentNode = editorView.state.doc.nodeAt(pos);
      if (!currentNode) return;

      const parser = new DOMParser();
      const inertDoc = parser.parseFromString(
        currentNode.attrs.html || "<span></span>",
        "text/html"
      );
      const sourceEl = (inertDoc.body.firstElementChild ??
        inertDoc.body) as HTMLElement;
      const imgs = Array.from(
        sourceEl.querySelectorAll<HTMLImageElement>("img")
      );
      const targetImg = imgs[imgIdx];
      if (!targetImg) return;

      targetImg.setAttribute("data-media-title", newTitle);
      targetImg.setAttribute("alt", newTitle);

      const tr = editorView.state.tr.setNodeMarkup(pos, undefined, {
        ...currentNode.attrs,
        html: sourceEl.outerHTML
      });
      editorView.dispatch(tr);

      const endlinkUrl = currentNode.attrs["data-media-widget"] as string;
      const mediaId = targetImg.getAttribute("data-media-id") ?? "";
      options.onMediaTitleChangeRef.current?.(endlinkUrl, mediaId, newTitle);
    };

    input.addEventListener("blur", commit);
    input.addEventListener("keydown", ke => {
      if (ke.key === "Enter") {
        ke.preventDefault();
        input.blur();
      } else if (ke.key === "Escape") {
        // Set committed before touching textContent so the blur that fires
        // when the input is removed from the DOM is a no-op.
        committed = true;
        titleBar.textContent = currentTitle;
      }
    });
  });
}
